import React, { FunctionComponent } from "react";
import styles from "./Carousel.module.scss";
import CarouselBlockProps from "./CarouselProps.interface";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import { useRef } from "react";
import Slide from "./components/Slide/Slide";
import getButtonStyles from "components/Button/helpers/getButtonStyles";
import { useSelector } from "react-redux";
import AliceCarousel from "react-alice-carousel";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";

const Carousel: FunctionComponent<CarouselBlockProps> = (
  props
): JSX.Element => {
  const wrapperClasses = getWrapperClasses(props.settings);
  const wrapperStyles = useWrapperStyles(props.settings);
  const { inViewElementRef, inViewClasses } = setInViewClasses(styles);
  const { splitClasses, splitStyles } = useSplitBackground(props);
  const settings = useSelector((state) => state.settings.content);
  const themeButtonStyles = getButtonStyles(settings?.theme, props?.variant);
  const slideRef = useRef<HTMLDivElement>(null);

  const headlineCaseClass = props.settings.headlineCase
    ? styles[`case_${props.settings.headlineCase}`]
    : undefined;

  const responsive = {
    0: { items: 1 },
    500: { items: 2 },
    568: { items: 3 },
    1024: { items: 4 },
  };

  const items =
    props.content.items &&
    props.content.items.length > 0 &&
    props.content.items.map((item) => (
      <Slide key={item.contentUdi} {...item} ref={slideRef} />
    ));

  return (
    <section
      className={combineClasses(
        styles.wrapper,
        inViewClasses,
        ...wrapperClasses
      )}
      style={{ ...wrapperStyles, ...splitStyles, ...themeButtonStyles }}
      ref={inViewElementRef}
    >
      <div className={combineClasses(styles.container, ...splitClasses)}>
        {/* Headline */}
        {props.content.headline ? (
          <DynamicHeading
            key={`head_${props.contentUdi}`}
            className={combineClasses(styles.headline, headlineCaseClass)}
            text={props.content.headline}
            headlineSize={props.settings.headlineSize}
            headlineWeight={props.settings.headlineWeight}
          />
        ) : null}

        <div className={styles.carousel}>
          <AliceCarousel
            items={items as any}
            paddingLeft={0}
            paddingRight={50}
            responsive={responsive}
            infinite={true}
            keyboardNavigation={true}
            mouseTracking={true}
            disableDotsControls={true}
            disableButtonsControls={true}
            activeIndex={0}
            autoPlay={props?.settings?.autoplay}
            autoPlayInterval={props?.settings?.interval ?? 1000}
            animationDuration={700}
            animationType="fadeout"
          />
        </div>
      </div>
      {props.settings?.backgroundMedia?.[0] &&
      props.settings?.backgroundMedia?.length > 0 ? (
        <BackgroundMedia
          key={props.contentUdi}
          {...props.settings?.backgroundMedia[0]}
        />
      ) : null}
    </section>
  );
};

export default Carousel;
